import { memo } from "react";
import {
  DestinationType,
  Kind,
  SourceType,
  useSourcesAndTypesQuery,
} from "../../../graphql/generated";
import {
  DialogResource,
  NewResourceDialog,
} from "../../../components/ResourceDialog";
import { useSnackbar } from "notistack";
import { ShowPageConfig } from ".";
import {
  BPConfiguration,
  BPResourceConfiguration,
} from "../../../utils/classes";
import { UpdateStatus } from "../../../types/resources";

type ResourceType = SourceType | DestinationType;

const AddSourcesComponent: React.FC<{
  configuration: NonNullable<ShowPageConfig>;
  refetch: () => {};
  setAddDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addDialogOpen: boolean;
}> = ({ configuration, refetch, setAddDialogOpen, addDialogOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useSourcesAndTypesQuery({
    onError() {
      enqueueSnackbar(
        "There was an error retrieving Sources and SourceTypes.",
        {
          variant: "error",
        },
      );
    },
  });

  async function onNewSourceSave(
    values: { [key: string]: any },
    sourceType: ResourceType,
  ) {
    const newSourceConfig = new BPResourceConfiguration();
    newSourceConfig.type = sourceType.metadata.name;
    newSourceConfig.setParamsFromMap(values);

    const updatedConfig = new BPConfiguration(configuration);
    updatedConfig.addSource(newSourceConfig);
    try {
      const update = await updatedConfig.apply();
      if (update.status === UpdateStatus.INVALID) {
        console.error(update);
        throw new Error("failed to add source to configuration.");
      }

      setAddDialogOpen(false);
      refetch();
    } catch (err) {
      enqueueSnackbar("Failed to save source.", {
        variant: "error",
      });
      console.error(err);
    }
  }

  async function addExistingSource(existingSource: DialogResource) {
    const config = new BPConfiguration(configuration);
    config.addSource({
      name: existingSource.metadata.name,
      disabled: existingSource.spec.disabled ?? false,
    });

    try {
      const update = await config.apply();
      if (update.status === UpdateStatus.INVALID) {
        console.error(update);
        throw new Error(
          `failed to update resource, got status ${update.status}`,
        );
      }

      setAddDialogOpen(false);
      refetch();
    } catch (err) {
      enqueueSnackbar("Failed to add Source.", { variant: "error" });
    }
  }

  return (
    <NewResourceDialog
      platform={configuration.metadata.labels.platform ?? "unknown"}
      kind={Kind.Source}
      resourceTypes={data?.sourceTypes ?? []}
      resources={data?.sources ?? []}
      open={addDialogOpen}
      onSaveNew={onNewSourceSave}
      onSaveExisting={addExistingSource}
      onClose={() => setAddDialogOpen(false)}
    />
  );
};

export const AddSourcesSection = memo(AddSourcesComponent);
